import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Invoice, Payment, InvoiceStatus, AgingBucket,
} from '@/types/financials';

// ─────────── Invoices ───────────

export type InvoiceFilters = {
  status?: InvoiceStatus;
  clientId?: string;
  orderId?: string;
  search?: string;
  limit?: number;
};

export function useInvoices(filters: InvoiceFilters = {}) {
  return useQuery({
    queryKey: ['invoices', filters],
    queryFn: async () => (await api.get<{ items: Invoice[] }>('/financials/invoices', { params: filters })).data.items,
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => (await api.get<Invoice>(`/financials/invoices/${id}`)).data,
    enabled: !!id,
  });
}

export type SaveInvoiceInput = Omit<Invoice,
  'id' | 'number' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' |
  'totals' | 'sentAt' | 'voidedAt' | 'voidReason' | 'lastReminderAt'
> & { totals?: Invoice['totals'] };

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveInvoiceInput) =>
      (await api.post<{ id: string; number: string }>('/financials/invoices', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['aging'] });
    },
  });
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveInvoiceInput>) =>
      (await api.patch(`/financials/invoices/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice', vars.id] });
      qc.invalidateQueries({ queryKey: ['aging'] });
    },
  });
}

export function useSendInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ ok: true; messageId: string }>(`/financials/invoices/${id}/send`)).data,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice', id] });
    },
  });
}

export function useSendReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ ok: true; messageId: string }>(`/financials/invoices/${id}/remind`)).data,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice', id] });
    },
  });
}

export function useVoidInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      (await api.post(`/financials/invoices/${id}/void`, { reason })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice', vars.id] });
      qc.invalidateQueries({ queryKey: ['aging'] });
    },
  });
}

export function useInvoiceFromOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) =>
      (await api.post<{ id: string; number: string }>('/financials/invoices/from-order', { orderId })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });
}

// ─────────── Payments ───────────

export type PaymentFilters = { clientId?: string; invoiceId?: string; limit?: number };

export function usePayments(filters: PaymentFilters = {}) {
  return useQuery({
    queryKey: ['payments', filters],
    queryFn: async () => (await api.get<{ items: Payment[] }>('/financials/payments', { params: filters })).data.items,
  });
}

export type RecordPaymentInput = {
  clientId: string;
  amount: number;
  method: Payment['method'];
  reference?: string;
  receivedDate: string;
  applications: Array<{ invoiceId: string; amount: number }>;
  notes?: string;
};

export function useRecordPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecordPaymentInput) =>
      (await api.post<{ id: string; number: string }>('/financials/payments', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['payments'] });
      qc.invalidateQueries({ queryKey: ['aging'] });
    },
  });
}

// ─────────── Aging ───────────

export type AgingSummary = {
  totalReceivable: number;
  buckets: Record<AgingBucket, number>;
  byClient: Array<{
    clientId: string;
    clientName: string;
    total: number;
    buckets: Record<AgingBucket, number>;
    oldestDueDate?: string;
  }>;
};

export function useAgingReport() {
  return useQuery({
    queryKey: ['aging'],
    queryFn: async () => (await api.get<AgingSummary>('/financials/aging')).data,
  });
}
